import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  EmojiEvents as BadgeIcon,
  Groups as ClubIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import UserService, { UserProfile } from '../services/userService';

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOwnProfile = currentUser?.id === userId;

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!userId) return;

      setLoading(true);
      setError(null);

      try {
        const profile = isOwnProfile
          ? await UserService.getCurrentUserProfile()
          : await UserService.getUserProfile(userId);

        setUserProfile(profile);
      } catch (error: any) {
        setError(error?.message || 'Failed to load user profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, isOwnProfile]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Loading Profile...
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !userProfile) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Profile
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isOwnProfile ? 'My Profile' : `${userProfile.name}'s Profile`}
          </Typography>
        </Box>

        {isOwnProfile && (
          <Button
            variant="outlined"
            onClick={() => navigate('/profile')}
            sx={{ textTransform: 'none' }}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        {/* Profile Header Card */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{
            height: 150,
            background: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
            position: 'relative'
          }} />

          <CardContent sx={{ p: 3, pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Avatar sx={{
                width: 120,
                height: 120,
                fontSize: '3rem',
                fontWeight: 700,
                bgcolor: '#1877f2',
                border: '4px solid white',
                mt: -8,
                mb: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {userProfile.profilePicture || userProfile.name.charAt(0).toUpperCase()}
              </Avatar>

              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1877f2' }}>
                {userProfile.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  icon={userProfile.role === 'Faculty' ? <SchoolIcon /> : <PersonIcon />}
                  label={userProfile.role}
                  color={userProfile.role === 'Faculty' ? 'secondary' : 'primary'}
                  sx={{ fontWeight: 600 }}
                />
                {!userProfile.isVerified && (
                  <Chip
                    label="Pending Verification"
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              {userProfile.goals && (
                <Typography variant="body1" color="text.secondary" sx={{
                  fontStyle: 'italic',
                  textAlign: 'center',
                  maxWidth: 600,
                  lineHeight: 1.6
                }}>
                  "{userProfile.goals}"
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information and Clubs Row */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3
          }}>
            {/* Basic Information */}
            <Box sx={{ flex: { md: 1 } }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> Basic Information
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <EmailIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {userProfile.email}
                        </Typography>
                      </Box>
                    </Box>

                    {userProfile.branch && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <BusinessIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Branch
                          </Typography>
                          <Typography variant="body1">
                            {userProfile.branch}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Clubs */}
            <Box sx={{ flex: { md: 1 } }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClubIcon /> Clubs ({userProfile.clubs.length})
                  </Typography>

                  {userProfile.clubs.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {userProfile.clubs.map((club, index) => (
                        <React.Fragment key={club.id}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: '#1877f2', width: 40, height: 40 }}>
                                {club.logo || club.name.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {club.name}
                                </Typography>
                              }
                              secondary={
                                <Chip
                                  label={club.memberRole}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', mt: 0.5 }}
                                />
                              }
                            />
                          </ListItem>
                          {index < userProfile.clubs.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      Not a member of any clubs yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Badges */}
          {userProfile.badges.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BadgeIcon /> Badges ({userProfile.badges.length})
                </Typography>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}>
                  {userProfile.badges.map((badge) => (
                    <Card key={badge.id} variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography sx={{ fontSize: '2rem', mb: 1 }}>
                          {badge.icon}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.5 }}>
                          {badge.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          {badge.description}
                        </Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Awarded by {badge.awardedBy}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(badge.dateAwarded)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserProfilePage;